import type React from "react";
import { useTranslation } from "react-i18next";
import type { BriefDetails } from "src/model/model";

interface AvatarAIDetailsProps {
  data: BriefDetails | Record<string, unknown>;
  mode: "plan" | "recap";
}

const ns = "pages.chat.interactionDetails";

/** Renderer for AvatarAIPersona and AvatarAISessionDebrief */
const AvatarAIDetails: React.FC<AvatarAIDetailsProps> = ({ data, mode }) => {
  const { t } = useTranslation();
  if (!data) return null;

  if (mode === "plan") {
    const bd = data as BriefDetails;
    const hasContent =
      bd.expectedPersonaName ||
      bd.expectedPersonaTone ||
      bd.expectedPersonaTraits?.length ||
      bd.expectedKeyTopics?.length ||
      bd.expectedSituationContext ||
      bd.expectedUserRole ||
      bd.expectedSuccessCriteria ||
      bd.expectedFailureCriteria;
    if (!hasContent) return null;

    return (
      <div className="space-y-1 text-[11px] text-gray-700">
        {bd.expectedPersonaName && (
          <div>
            <span className="font-medium text-gray-500">{t(`${ns}.personaName`)} : </span>
            {bd.expectedPersonaName}
          </div>
        )}
        {bd.expectedPersonaTone && (
          <div>
            <span className="font-medium text-gray-500">{t(`${ns}.personaTone`)} : </span>
            {bd.expectedPersonaTone}
          </div>
        )}
        {bd.expectedPersonaTraits && bd.expectedPersonaTraits.length > 0 && (
          <div className="flex flex-wrap gap-1">
            <span className="font-medium text-gray-500 shrink-0">{t(`${ns}.personaTraits`)} :</span>
            {bd.expectedPersonaTraits.map((trait, i) => (
              <span key={i} className="px-1.5 py-0.5 rounded bg-purple-50 text-purple-700">
                {trait}
              </span>
            ))}
          </div>
        )}
        {bd.expectedKeyTopics && bd.expectedKeyTopics.length > 0 && (
          <div>
            <span className="font-medium text-gray-500">{t(`${ns}.keyTopics`)} : </span>
            {bd.expectedKeyTopics.join(", ")}
          </div>
        )}
        {bd.expectedSituationContext && (
          <div>
            <span className="font-medium text-gray-500">{t(`${ns}.situationContext`)} : </span>
            <span className="whitespace-pre-wrap">{bd.expectedSituationContext}</span>
          </div>
        )}
        {bd.expectedUserRole && (
          <div>
            <span className="font-medium text-gray-500">{t(`${ns}.userRole`)} : </span>
            {bd.expectedUserRole}
          </div>
        )}
        {bd.expectedSuccessCriteria && (
          <div>
            <span className="font-medium text-gray-500">{t(`${ns}.successCriteria`)} : </span>
            <span className="whitespace-pre-wrap">{bd.expectedSuccessCriteria}</span>
          </div>
        )}
        {bd.expectedFailureCriteria && (
          <div>
            <span className="font-medium text-gray-500">{t(`${ns}.failureCriteria`)} : </span>
            <span className="whitespace-pre-wrap">{bd.expectedFailureCriteria}</span>
          </div>
        )}
      </div>
    );
  }

  // Recap mode: real generated content
  const d = data as Record<string, unknown>;
  return (
    <div className="space-y-1 text-[11px] text-gray-700">
      {Boolean(d.name) && (
        <div>
          <span className="font-medium text-gray-500">{t(`${ns}.personaName`)} : </span>
          {String(d.name)}
        </div>
      )}
      {Boolean(d.context_prompt) && (
        <div>
          <span className="font-medium text-gray-500">{t(`${ns}.context`)} : </span>
          <span className="whitespace-pre-wrap">{String(d.context_prompt)}</span>
        </div>
      )}
      {Boolean(d.avatar_role) && (
        <div>
          <span className="font-medium text-gray-500">{t(`${ns}.avatarRole`)} : </span>
          <span className="whitespace-pre-wrap">{String(d.avatar_role)}</span>
        </div>
      )}
      {Boolean(d.user_role) && (
        <div>
          <span className="font-medium text-gray-500">{t(`${ns}.userRole`)} : </span>
          {String(d.user_role)}
        </div>
      )}
    </div>
  );
};

export default AvatarAIDetails;
